import type { MarketStatus, PublicPredictionMarket } from '../services/interfaces/market.types';
import { isMoneylineTwoWay } from '../services/interfaces/market.types';

import type { Side } from './pricing';

export const MIN_LIQUIDITY_TO_TRADE_DOLLARS = 1;

export type MarketType = 'spread' | 'total' | 'moneyline';
export type TeamType = 'home' | 'away';

export function getMarketAndSideForSelection(params: {
  markets: PublicPredictionMarket[];
  marketType: MarketType;
  teamId: string;
  teamType: TeamType;
}): { market: PublicPredictionMarket | undefined; side: Side } {
  const { markets, marketType, teamId, teamType } = params;

  if (marketType === 'moneyline') {
    const twoWay = markets.find((m) => m.type === 'moneyline' && isMoneylineTwoWay(m));
    if (twoWay && twoWay.type === 'moneyline' && isMoneylineTwoWay(twoWay)) {
      return {
        market: twoWay,
        side: twoWay.yes_team_id === teamId ? 'yes' : 'no',
      };
    }
    return {
      market: markets.find(
        (m) => m.type === 'moneyline' && !isMoneylineTwoWay(m) && m.team_id === teamId
      ),
      side: 'yes',
    };
  }

  if (marketType === 'spread') {
    const market = markets.find((m) => m.type === 'spread');
    if (market && market.type === 'spread') {
      return {
        market,
        side: market.yes_team_id === teamId ? 'yes' : 'no',
      };
    }
    return { market: undefined, side: 'yes' };
  }

  const market = markets.find((m) => m.type === 'total');
  return {
    market,
    side: teamType === 'away' ? 'yes' : 'no',
  };
}

const OPEN_MARKET_STATUS: MarketStatus = 'active';

function getMarketStatus(market: PublicPredictionMarket): MarketStatus | undefined {
  return (market as { status?: MarketStatus }).status;
}

export function isClosedMarketStatus(status: MarketStatus | undefined): boolean {
  return status !== undefined && status !== OPEN_MARKET_STATUS;
}

export function isMarketClosed(market: PublicPredictionMarket): boolean {
  return isClosedMarketStatus(getMarketStatus(market));
}

function getSideLiquidityDollars(market: PublicPredictionMarket, side: Side): number | undefined {
  return side === 'yes' ? market.yes_liquidity : market.no_liquidity;
}

export function isLockedByLowLiquidity(params: {
  market: PublicPredictionMarket;
  side: Side;
  minDollars?: number;
}): boolean {
  const { market, side, minDollars = MIN_LIQUIDITY_TO_TRADE_DOLLARS } = params;

  if (isMarketClosed(market)) {
    return true;
  }

  const liquidityDollars = getSideLiquidityDollars(market, side);

  if (liquidityDollars === undefined) {
    return true;
  }

  return liquidityDollars < minDollars;
}
